// bk17 Vino Tushi — bookie catalog card (burgundy + cream).
//
// Opera-house formal: an outer deep-burgundy foil mat surrounds a thick
// cream inner card with a thin gold/burgundy hairline. The focal element
// is a vintage cameo wreath in inline SVG. Names render in Cormorant
// italic for romance; the caption rides at the bottom in small caps.
import { Photo, type LayoutCtx } from "./_shared";

export function BookieBurgundyPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
  photo,
}: LayoutCtx) {
  // Deeper burgundy mat. Mix accent into ink so the gradient reads as a
  // real foil even if the operator nudges the accent.
  const mat = `linear-gradient(135deg, ${accent} 0%, color-mix(in srgb, ${accent} 65%, #000 35%) 50%, ${accent} 100%)`;
  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: mat,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          position: "relative",
          boxShadow: `inset 0 0 0 1px ${accent}55, inset 0 0 0 5px ${bg}, inset 0 0 0 6px ${accent}66`,
        }}
      >
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TAKLIFNOMA
        </div>

        {/* Cameo wreath medallion */}
        <div
          style={{
            width: "60%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
            borderRadius: "50%",
            background: `radial-gradient(circle at 50% 45%, ${bg} 0%, ${accent}10 70%, ${bg})`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: `inset 0 0 0 1px ${accent}66, 0 0 0 2px ${bg}, 0 0 0 3px ${accent}33`,
            overflow: "hidden",
          }}
        >
          <Photo
            src={photo}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", borderRadius: "50%" }}
            fallback={
              <svg viewBox="0 0 100 100" style={{ width: "78%", height: "78%" }} aria-hidden>
                {/* Outer wreath ring */}
                <circle cx="50" cy="50" r="34" fill="none" stroke={accent} strokeWidth="0.6" opacity="0.5" />
                {/* Olive-leaf wreath halves */}
                {Array.from({ length: 9 }).map((_, i) => {
                  const a = -100 + i * 14;
                  return (
                    <g key={`L${i}`} transform={`rotate(${a} 50 50) translate(0 -34)`}>
                      <ellipse cx="0" cy="0" rx="4" ry="2" fill={accent} opacity="0.7" />
                    </g>
                  );
                })}
                {Array.from({ length: 9 }).map((_, i) => {
                  const a = 100 - i * 14;
                  return (
                    <g key={`R${i}`} transform={`rotate(${a} 50 50) translate(0 -34)`}>
                      <ellipse cx="0" cy="0" rx="4" ry="2" fill={accent} opacity="0.7" />
                    </g>
                  );
                })}
                {/* Wine grapes cluster at top */}
                {[
                  [50, 22], [46, 26], [54, 26], [42, 30], [50, 30], [58, 30], [46, 34], [54, 34],
                ].map(([x, y], i) => (
                  <circle key={i} cx={x} cy={y} r="2.2" fill={accent} opacity="0.85" />
                ))}
                {/* Initial-script flourish in middle */}
                <text
                  x="50"
                  y="62"
                  textAnchor="middle"
                  fontFamily="serif"
                  fontStyle="italic"
                  fontSize="22"
                  fill={accent}
                  opacity="0.75"
                >
                  V
                </text>
              </svg>
            }
          />
        </div>

        <div
          style={{
            fontFamily: font,
            fontStyle: "italic",
            fontSize: 30,
            lineHeight: 1.05,
            fontWeight: 500,
            color: ink,
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TAKLIFNOMA · {date.toUpperCase()}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
